import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Cross from '@deliveroo/icon-set/react/Cross';
import Icon from '@deliveroo/consumer-component-library/components/Icon';
import Button from '@deliveroo/consumer-component-library/components/Button';
import P from '@deliveroo/consumer-component-library/components/P';
import Heading from '@deliveroo/consumer-component-library/components/Heading';
import { DIALOG } from '../../containers/OrderHelpTemplateContainer/templateTypes';

import i18n from '../../utils/i18n';
import getSafe from '../../utils/getSafe';
import styles from './styles.scss';

/**
 * A generic dynamic actions template to render a dialog over the current template.
 */
function OrderHelpTemplateDialog({
  data: { dialogTitle, dialogText, updateInteractionOnClose },
  actionId,
  requestInteraction,
  closeInteractionDialog,
  isDialogOnlyHelpPage,
  iframe,
  finishInteraction,
}) {
  const fullscreen = !iframe && isDialogOnlyHelpPage;

  const onClose = () => {
    if (updateInteractionOnClose) {
      requestInteraction({
        parentActionId: actionId,
        parentActionTemplate: DIALOG,
        templateData: {},
      });
    }
    if (isDialogOnlyHelpPage) {
      finishInteraction();
    } else {
      closeInteractionDialog();
    }
  };

  const onOverlayClick = (event) => {
    // A check to make sure we're only clicking the overlay (since the content is nested)
    if (
      getSafe(() => event.target.className) &&
      event.target.className.includes('OrderHelpTemplateDialog-overlay') &&
      !fullscreen
    ) {
      onClose();
    }
  };

  // Render stolen from https://github.com/deliveroo/design-system/blob/master/packages/consumer-component-library/src/components/Modal/index.js
  return (
    /* eslint-disable-next-line jsx-a11y/click-events-have-key-events */
    <div
      onClick={onOverlayClick}
      role="button"
      tabIndex="-1"
      className={classnames('OrderHelpTemplateDialog-overlay', styles.overlay, {
        [styles['hide-overlay']]: isDialogOnlyHelpPage,
        [styles.fullscreen]: fullscreen,
      })}
    >
      <div className={styles['content-wrapper']}>
        <div className={styles['inner-content-wrapper']}>
          {/* eslint-disable-next-line react/button-has-type */}
          <button className={styles['floating-close-button']} onClick={onClose}>
            <Icon component={Cross} />
          </button>

          <div className={styles.content}>
            <div className={styles['content-padding']}>
              {!fullscreen && (
                <Heading level={4} size="small" spacing="0 0 medium 0">
                  {dialogTitle}
                </Heading>
              )}

              <P>{dialogText}</P>
            </div>
          </div>

          <div className={styles.footer}>
            <Button type="primary" isBlock onClick={onClose}>
              {i18n.gettext('Ok')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

OrderHelpTemplateDialog.propTypes = {
  /**
   * Which help screen the template is currently on.
   */
  actionId: PropTypes.string.isRequired,
  /**
   * Template data to fill out the generic template.
   */
  data: PropTypes.shape({
    dialogTitle: PropTypes.string,
    dialogText: PropTypes.string,
    updateInteractionOnClose: PropTypes.bool,
  }),
  /**
   * The dispatch action that will be triggered onClick for each OrderHelpActionRows.
   * This will be used to update the dynamic action and grab the next self help page
   */
  requestInteraction: PropTypes.func.isRequired,
  /**
   * The action to close the dialog
   */
  closeInteractionDialog: PropTypes.func.isRequired,
  /**
   * If the dialog is the only thing that's showing (and there's no self help under it)
   */
  isDialogOnlyHelpPage: PropTypes.bool,
  iframe: PropTypes.bool,
  finishInteraction: PropTypes.func.isRequired,
};

OrderHelpTemplateDialog.defaultProps = {
  data: {},
  isDialogOnlyHelpPage: false,
  iframe: false,
};

export default OrderHelpTemplateDialog;
